import React, { FC, useMemo } from 'react';
import { useQuery } from 'react-query';
import { useHistory } from 'react-router';
import { useTranslation } from 'react-i18next';
import { CellProps, Column } from 'react-table';
import { FaEdit, FaRegEnvelope, FaRegFile, FaPlus } from 'react-icons/fa';
import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';
import { useModal } from 'store/modal';
import { PermissionKeys } from 'store/types';
import { useLanguageContext } from 'store/language';
import { usePermissionsContext } from 'store/permissions';
import { useActiveClientContext } from 'store/active-client';
import { getSubdomain } from 'utils/helpers/routing';
import useWindowWidth from 'utils/helpers/useWindowWidth';
import { useFilterPersistence, applyFiltersToOptions } from 'utils/helpers/useFilterPersistence';
import Badge from 'components/Badge';
import Button from 'components/Button';
import FetchTable from 'components/table/FetchTable';
import TooltipWrapper from 'components/TooltipWrapper';
import { buildFilters } from 'components/table/utils/filtersConfig';
import { ISelectOptionProps } from 'components/form/types';
import { formatDate } from 'utils/helpers/formatters';
import copyToClipboard from 'utils/helpers/copyToClipboard';
import { buildClientUrl, buildClientEntity } from 'utils/helpers/client';
import { renderAccessor, renderFullNameTooltip } from 'utils/helpers/user';
import { IEmployee } from '../types';
import InnerWrapper from '../../InnerWrapper';

const FILTERS_STORAGE_KEY = 'employees-list-filters';

const Screen: FC = () => {
  const { t } = useTranslation();
  const history = useHistory();
  const { client } = useActiveClientContext();
  const { language } = useLanguageContext();
  const { showModal } = useModal();
  const { checkPermission } = usePermissionsContext();
  const isEN = language === 'en';
  const windowWidth = useWindowWidth();
  const isOperator = getSubdomain() === 'operators';
  const canCreateDoc = !isOperator && checkPermission(PermissionKeys.CAN_CREATE_DOCUMENTS);

  const {
    isLoading: isLoadingUserDropdowns,
    error: errorUserDropdowns,
    data: dropdowns,
  } = useQuery<{ [key: string]: ISelectOptionProps[] }, Error>(
    [buildClientEntity('user-dropdowns', client)],
    (): Promise<{ [key: string]: ISelectOptionProps[] }> => {
      return API.get('users/dropdown-options').then(res => res.data && res.data[language || 'bg']);
    }
  );

  const { initialFilters, handleFiltersChange } = useFilterPersistence(FILTERS_STORAGE_KEY, dropdowns);

  const createEmployee = () => {
    const url = buildClientUrl('/structure/employees/create', client);
    history.push(url);
  };

  const importEmployee = () => {
    const url = buildClientUrl('/structure/employees/import', client);
    history.push(url);
  };

  const resendActivation = (id: number) =>
    API.post(`users/${id}/resend-activation-email`)
      .then(() => {
        showModal({
          type: 'success',
          content: t('auth.accountActivation.resendActivationSuccessMsg'),
        });
      })
      .catch(err => {
        showModal({
          type: 'error',
          content: createErrorFromResponse(err).message,
        });
      });

  const columns: Array<Column<IEmployee>> = [
    {
      Header: 'ID',
      accessor: 'id',
    },
    {
      Header: t('structure.employees.name') || '',
      /*
        According to the documentation `accessor` can accept a function,
        but apparantly the typings are wrong
        https://react-table-v7.tanstack.com/docs/api/useTable#column-options
      */
      accessor: ((row: IEmployee) => renderAccessor(row, 'first_name', isEN)) as unknown as string,
      width: '8%',
      minWidth: 90,
      Cell: ({ row, value }: CellProps<IEmployee>) => {
        return (
          <TooltipWrapper
            id="tooltip-name"
            overlay={renderFullNameTooltip(row.original, isEN)}
          >
            <span className="text-break">{value}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: `${t('structure.employees.lastName')}` || '',
      /*
        According to the documentation `accessor` can accept a function,
        but apparantly the typings are wrong
        https://react-table-v7.tanstack.com/docs/api/useTable#column-options
      */
      accessor: ((row: IEmployee) => renderAccessor(row, 'last_name', isEN)) as unknown as string,
      width: '8%',
      minWidth: 90,
      Cell: ({ row, value }: CellProps<IEmployee>) => {
        return (
          <TooltipWrapper
            id="tooltip-last-name"
            overlay={renderFullNameTooltip(row.original, isEN)}
          >
            <span className="text-break">{value}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: 'Email',
      accessor: 'email',
      width: '16%',
      disableSortBy: true,
      Cell: ({ value }) => {
        const emailPreview = value.length > 30 ? `${value.substr(0, 30)}...` : value;

        return (
          <TooltipWrapper
            id="tooltip-email"
            overlay={`${t('common.copyToClipboard')}: ${value}`}
          >
            <span onClick={() => copyToClipboard(value)}>{emailPreview}</span>
          </TooltipWrapper>
        );
      },
    },
    {
      Header: t('structure.employees.groups') || '',
      accessor: 'groups',
      width: '12%',
      Cell: ({ value }) => {
        const fullGroups = value.map((group: any) => group.name).join(', ');
        const groupsPreview = fullGroups.length > 20 ? `${fullGroups.substr(0, 20)}...` : fullGroups;

        return (
          <TooltipWrapper
            id="tooltip-groups"
            overlay={`${t('common.copyToClipboard')}: ${fullGroups}`}
          >
            <span onClick={() => copyToClipboard(fullGroups)}>{groupsPreview}</span>
          </TooltipWrapper>
        );
      },
      disableSortBy: true,
    },
    {
      Header: t('structure.employees.type') || '',
      accessor: 'type',
      width: '8%',
      Cell: cell => {
        const userType = cell.row.original.type;

        const getVariant = (type: string): 'primary' | 'secondary' | 'warning' => {
          if (type === 'employee') return 'primary';
          if (type === 'employee_candidate') return 'warning';
          return 'secondary';
        };

        if (dropdowns && dropdowns.type) {
          const type = dropdowns.type.find((tp: ISelectOptionProps) => tp.value === userType);
          return (
            <Badge
              label={type?.label}
              variant={getVariant(userType)}
            />
          );
        }
        return (
          <Badge
            label={userType}
            variant={getVariant(userType)}
          />
        );
      },
      disableSortBy: true,
    },
    {
      Header: t('structure.employees.createdAt') || '',
      accessor: 'created_at',
      width: '8%',
      Cell: cell => (
        <TooltipWrapper
          id="tooltip-date-created"
          overlay={formatDate(new Date(cell.row.original.created_at), 'dd.mm.Y hh:ii:ss')}
        >
          {formatDate(new Date(cell.row.original.created_at), 'dd.mm.Y')}
        </TooltipWrapper>
      ),
    },
    {
      Header: t('structure.employees.status') || '',
      accessor: 'account_status',
      width: windowWidth < 1600 ? '8%' : '5%',
      disableSortBy: true,
      Cell: cell => {
        const userAccStatus = cell.row.original.account_status;
        if (dropdowns && dropdowns.account_status) {
          const status = dropdowns.account_status.find((s: ISelectOptionProps) => s.value === userAccStatus);
          if (status) {
            const variant: {
              [key: string]: 'primary' | 'danger' | 'info';
            } = {
              active: 'primary',
              inactive: 'danger',
              released: 'info',
            };
            return (
              <Badge
                label={status?.label}
                variant={variant[status.value.toString()]}
              />
            );
          }
        }

        return (
          <Badge
            label={userAccStatus}
            variant="primary"
          />
        );
      },
    },
    {
      Header: t('structure.employees.twoFaStatus') || '',
      accessor: '2fa',
      width: windowWidth < 1600 ? '8%' : '5%',
      disableSortBy: true,
      Cell: cell => {
        const is2FaActive = cell.row.original.two_fa_confirmed;

        if (is2FaActive)
          return (
            <Badge
              label={t('structure.employees.active')}
              variant="primary"
            />
          );

        return (
          <Badge
            label={t('structure.employees.inactive')}
            variant="danger"
          />
        );
      },
    },
    {
      Header: () => <div className="text-right">{t('table.actions')}</div>,
      accessor: 'actions',
      width: '12%',
      disableSortBy: true,
      Cell: cell => {
        const {
          id,
          can_edit: canEdit,
          account_activated: accountActivated,
          account_status: accountStatus,
          type,
          created_at: createdAt,
        } = cell.row.original;
        const isEmployee = type.includes('employee');
        const url = buildClientUrl(`/structure/employees/edit/${id}`, client);
        return (
          <div className="text-right">
            {isEmployee && (
              <>
                <span className="ml-2">
                  <TooltipWrapper
                    id="tooltip-create-doc"
                    overlay={t(canCreateDoc ? 'common.createEmpDoc' : 'docWizard.operatorMessage')}
                  >
                    <Button
                      btnType="light"
                      size="sm"
                      outlined
                      disabled={!canCreateDoc}
                      onClick={() =>
                        history.push({
                          pathname: '/documents-wizard/step-1',
                          state: {
                            employeeId: id,
                          },
                        })
                      }
                    >
                      <FaPlus size={14} />
                    </Button>
                  </TooltipWrapper>
                </span>
                <span className="ml-2">
                  <TooltipWrapper
                    id="tooltip-emp-docs"
                    overlay={t('common.viewEmpDocs')}
                  >
                    <Button
                      btnType="info"
                      size="sm"
                      outlined
                      onClick={() =>
                        history.push({
                          pathname: isOperator ? buildClientUrl('/reports/documents', client) : '/reports/documents',
                          state: {
                            employeeId: id,
                            employeeCreatedAt: createdAt,
                          },
                        })
                      }
                    >
                      <FaRegFile size={14} />
                    </Button>
                  </TooltipWrapper>
                </span>
              </>
            )}
            {!accountActivated && accountStatus === 'active' && (
              <span className="ml-2">
                <TooltipWrapper
                  id="tooltip-resend"
                  overlay={canEdit ? t('auth.accountActivation.resendEmailActivation') : t('common.noPermission')}
                >
                  <Button
                    btnType="secondary"
                    size="sm"
                    outlined
                    disabled={!canEdit}
                    onClick={() => resendActivation(id)}
                  >
                    <FaRegEnvelope size={14} />
                  </Button>
                </TooltipWrapper>
              </span>
            )}
            <span className="ml-2">
              <TooltipWrapper
                id="tooltip-edit"
                overlay={canEdit ? t('common.edit') : t('common.noPermission')}
              >
                <Button
                  ariaLabel="Edit Employee"
                  btnType="primary"
                  size="sm"
                  outlined
                  disabled={!canEdit}
                  onClick={() => history.push(url)}
                >
                  <FaEdit size={14} />
                </Button>
              </TooltipWrapper>
            </span>
          </div>
        );
      },
    },
  ];

  const tableFilters = useMemo(() => {
    const groupOptions = applyFiltersToOptions(dropdowns?.group, 'group', initialFilters);

    return [
      buildFilters({
        name: 'account_status',
        placeholder: t('table.statusFilter'),
        options: applyFiltersToOptions(dropdowns?.account_status, 'account_status', initialFilters),
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'type',
        placeholder: t('table.typeFilter'),
        ariaLabel: 'Employee type',
        options: applyFiltersToOptions(dropdowns?.type, 'type', initialFilters),
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
      buildFilters({
        name: 'group',
        placeholder: t('table.groupFilter'),
        options: groupOptions,
        isMulti: true,
        showTotal: true,
        allowSelectAll: true,
      }),
    ];
  }, [dropdowns, t, initialFilters]);

  return (
    <InnerWrapper error={errorUserDropdowns}>
      <FetchTable<IEmployee>
        entityName={buildClientEntity('users', client)}
        entityEndpoint="users"
        loading={isLoadingUserDropdowns}
        columns={columns}
        onAdd={createEmployee}
        onImport={importEmployee}
        addBtnLabel={t('structure.employees.addBtnLabel')}
        pagination
        filters={tableFilters}
        onFiltersChange={handleFiltersChange}
        hasPermission={[
          PermissionKeys.CAN_MANAGE_EMPLOYERS,
          PermissionKeys.CAN_MANAGE_MODERATORS,
          PermissionKeys.CAN_MANAGE_EMPLOYEES,
        ]}
        hasImportPermission={checkPermission(PermissionKeys.CAN_IMPORT_EMPLOYEES)}
        showFilterWarning
      />
    </InnerWrapper>
  );
};

export default Screen;
